// Consent storage + Google Consent Mode v2 plumbing for the marketing site.
//
// Two rules from the CNIL's guidance on trackers drive the design:
//
//  1. Nothing loads before an explicit choice. gtag.js itself is only injected
//     once analytics consent is granted — Consent Mode "denied" still pings
//     Google's servers, and GA4 is not on the CNIL's list of tools exempt from
//     consent, so "load it denied" is not a defensible position here.
//  2. A choice is remembered, but not forever: an acceptance is re-asked after
//     13 months (max consent lifetime), a refusal after 6 months (so we are not
//     nagging someone who already said no).

export const GA_MEASUREMENT_ID = "G-7KHY3SRD3H";

// The tag only ever loads in a production build. The banner still runs in dev
// so the consent UX stays testable there — it simply has nothing to switch on,
// and sendPageView() logs instead of sending.
export const GA_ENABLED = process.env.NODE_ENV === "production";

export const CONSENT_STORAGE_KEY = "woozoo.consent";

export type ConsentStatus = "unset" | "granted" | "denied";

const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
export const GRANTED_TTL_MS = 13 * MONTH_MS;
export const DENIED_TTL_MS = 6 * MONTH_MS;

type StoredConsent = { v: 1; analytics: boolean; ts: number };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function readConsent(): ConsentStatus {
  if (typeof window === "undefined") return "unset";
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return "unset";
    const parsed = JSON.parse(raw) as StoredConsent | null;
    if (!parsed || parsed.v !== 1 || typeof parsed.analytics !== "boolean") return "unset";
    const ttl = parsed.analytics ? GRANTED_TTL_MS : DENIED_TTL_MS;
    if (!Number.isFinite(parsed.ts) || Date.now() - parsed.ts > ttl) return "unset";
    return parsed.analytics ? "granted" : "denied";
  } catch {
    return "unset";
  }
}

export function writeConsent(analytics: boolean): void {
  try {
    const value: StoredConsent = { v: 1, analytics, ts: Date.now() };
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private mode / storage disabled — the choice just won't outlive the tab,
    // which means we ask again next visit. Failing closed is the right default.
  }
}

/** Mirror the choice into Consent Mode so a tag already on the page reacts. */
export function updateConsentMode(analytics: boolean): void {
  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
  });
}

/**
 * Withdrawing consent has to actually remove what was set, not just stop the
 * next hit. GA writes `_ga` and `_ga_<stream>` on the registrable domain.
 */
export function clearAnalyticsCookies(): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const parent = "." + host.split(".").slice(-2).join(".");
  for (const chunk of document.cookie.split(";")) {
    const name = chunk.split("=")[0]?.trim();
    if (!name || !name.startsWith("_ga")) continue;
    for (const domain of [undefined, host, parent]) {
      const scope = domain ? `; domain=${domain}` : "";
      document.cookie = `${name}=; Max-Age=0; path=/${scope}`;
    }
  }
}

/**
 * One page_view per real route change.
 *
 * The query string is deliberately dropped: /confirm carries the waitlist
 * confirmation token, and there is no reason for Google to ever receive it.
 * Language lives in ?lang= too, but GA4 already reports browser language.
 */
export function sendPageView(pathname: string): void {
  const pageLocation = window.location.origin + pathname;
  if (!GA_ENABLED) {
    console.debug("[analytics] page_view (dev — not sent):", pageLocation);
    return;
  }
  window.gtag?.("event", "page_view", {
    page_location: pageLocation,
    page_title: document.title,
  });
}

/**
 * Runs before anything else on the page (next/script beforeInteractive).
 *
 * Everything here only queues into dataLayer — no network. If consent is never
 * granted, gtag.js is never injected and the queue is simply never drained.
 * A previously stored acceptance is replayed synchronously so the very first
 * page_view isn't attributed under a "denied" state.
 */
export const CONSENT_BOOTSTRAP = `
(function () {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted'
  });

  var granted = false;
  try {
    var raw = localStorage.getItem(${JSON.stringify(CONSENT_STORAGE_KEY)});
    if (raw) {
      var stored = JSON.parse(raw);
      if (stored && stored.v === 1 && stored.analytics === true &&
          Date.now() - stored.ts <= ${GRANTED_TTL_MS}) {
        granted = true;
      }
    }
  } catch (e) { /* storage unavailable — stay denied */ }

  if (granted) gtag('consent', 'update', { analytics_storage: 'granted' });

  gtag('js', new Date());
  gtag('config', ${JSON.stringify(GA_MEASUREMENT_ID)}, {
    // Views are sent by hand on pathname change, so the hash links on the
    // one-page home (#b2b, #home) don't each count as a page view.
    send_page_view: false,
    // GA defaults _ga to 2 years; the CNIL caps tracker lifetime at 13 months.
    cookie_expires: ${13 * 30 * 24 * 60 * 60}
  });
})();
`;
